<script setup lang="ts">
import type { NavbarItem } from '~/types/navbar'

interface NavbarDropdownProps {
  contentKey: NavbarItem
  title?: string
  right?: boolean
  alwaysShowContentOnMobile?: boolean
  srLabel?: string
}

const props = defineProps<NavbarDropdownProps>()

const activeDropdown = defineModel<NavbarItem | undefined>('active', { required: true })

const viewport = useViewport()

const { t, te } = useI18n()

const hideButton = computed(
  () => props.alwaysShowContentOnMobile && viewport.isLessThan('desktop')
)
const isActive = computed(
  () => activeDropdown.value === props.contentKey || hideButton.value
)

const setActiveDropdown = (dropdown: NavbarItem, value?: boolean) => {
  if (value == undefined) {
    activeDropdown.value = activeDropdown.value === dropdown ? undefined : dropdown
  } else if (value) {
    activeDropdown.value = dropdown
  } else {
    activeDropdown.value = undefined
  }
}

const dropdown = useTemplateRef('dropdown')
const dropdownIsHovered = useElementHover(dropdown)
watch(dropdownIsHovered, (newValue) => {
  if (viewport.isGreaterThan('widescreen')) {
    setActiveDropdown(props.contentKey, newValue)
  }
})
</script>

<template>
  <div ref="dropdown" class="navbar-tab navbar-item p-0">
    <VHtmlButton
      v-if="!hideButton"
      :id="`dropdown-trigger-${contentKey}`"
      :aria-expanded="isActive"
      :aria-controls="`dropdown-menu-${contentKey}`"
      class="navbar-link navbar-header"
      :class="isActive && 'active'"
      :aria-label="srLabel && te(srLabel) ? t(srLabel) : srLabel"
      @click="setActiveDropdown(contentKey)"
      @keyup.esc="setActiveDropdown(contentKey, false)"
    >
      <slot name="title">
        <template v-if="title">
          {{ te(title) ? t(title) : title }}
        </template>
      </slot>
    </VHtmlButton>

    <div
      v-show="isActive"
      :id="`dropdown-menu-${contentKey}`"
      class="navbar-dropdown"
      :class="[
        right && !hideButton && 'right-0 left-[unset] max-lg:-ml-4',
        hideButton && 'pt-0'
      ]"
      :aria-labelledby="`dropdown-trigger-${contentKey}`"
    >
      <slot :show-on-mobile="hideButton" />
    </div>
  </div>
</template>

<style scoped></style>
